import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import { Box, IconButton, Switch, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';
import OrderHistoryDialog from '../../components/dashboard/admin/catalog/OrderHistoryDialog';
import PreviouslyOrderedColumn from '../../components/dashboard/orders/order/PreviouslyOrderedColumn';
import { useUpdateOrderItemRedux } from '../../hooks/useUpdateOrderItemRedux';
import { useSelector } from '../../redux/store';
import { cloudinaryFull, cloudinaryThumbnail } from '../../utils/formatCloudinary';
import { fCurrency, fifthDecimalCurrency } from '../../utils/formatNumber';
import { fMonthDayYear } from '../../utils/formatTime';
import Image from '../image';
import Lightbox from '../lightbox';
import { useScopeCheck } from '../scopes';
import Category from './Category';
import ProductItem from './ProductItem';
import QuantityField from './QuantityField';
import SubCategory from './SubCategory';
import UOMDropdown from './UOMDropdown';
import VendorField from './VendorField';

export default function RenderImage({ params }) {
  RenderImage.propTypes = {
    params: PropTypes.object,
  };
  const { pictures } = params.row;
  const [selectedImage, setSelectedImage] = useState(-1);
  const handleOpenLightbox = () => {
    setSelectedImage(0);
  };
  const handleCloseLightbox = () => {
    setSelectedImage(-1);
  };
  return (
    <>
      <Image
        alt="attachment"
        src={
          pictures?.length
            ? cloudinaryThumbnail(pictures?.[0])
            : cloudinaryThumbnail(
              'https://res.cloudinary.com/agoraserv/image/upload/v1738258682/No%20Image%20Animation.gif'
            )
        }
        sx={{
          cursor: 'pointer',
          maxWidth: '45px',
          maxHeight: '45px',
          '&:hover': {
            opacity: 0.9,
          },
        }}
        onClick={() => handleOpenLightbox()}
      />
      <Lightbox
        index={0}
        slides={pictures?.map((picture) => ({ src: cloudinaryFull(picture) }))}
        open={selectedImage >= 0}
        close={handleCloseLightbox}
      />
    </>
  );
}
export function RenderImageFromUrls({ params }) {
  RenderImageFromUrls.propTypes = {
    params: PropTypes.object,
  };
  const { pictureUrls } = params.row;
  const [selectedImage, setSelectedImage] = useState(-1);
  const handleOpenLightbox = () => {
    setSelectedImage(0);
  };

  const handleCloseLightbox = () => {
    setSelectedImage(-1);
  };
  return (
    <>
      <Image
        alt="attachment"
        src={pictureUrls?.[0]}
        sx={{
          cursor: 'pointer',
          maxWidth: '45px',
          maxHeight: '45px',
          '&:hover': {
            opacity: 0.9,
          },
        }}
        onClick={() => handleOpenLightbox()}
      />
      <Lightbox
        index={0}
        slides={pictureUrls?.map((url) => ({ src: url }))}
        open={selectedImage >= 0}
        close={handleCloseLightbox}
      />
    </>
  );
}
export function DeletedItemTooltip({ children, deleted}) {
  return <Tooltip title={deleted ? 'This item has been deleted' : ''} arrow location="top">
    <Box component="span" sx={{ pointerEvents: 'auto', width: '100%', height: '100%', alignContent: 'center' }}>
      {children}
    </Box>
  </Tooltip>
}

function ApprovedCell({ row, disabled = false }) {
  ApprovedCell.propTypes = {
    row: PropTypes.object,
    disabled: PropTypes.bool,
  };
  const hasAccess = useScopeCheck(['Edit-Order'], true);

  const updateOrderItemRedux = useUpdateOrderItemRedux();

  return <Tooltip title={row.punchoutOrderLocked ? "The vendor doesn't allow changes to punchout orders, to make changes, you must place a new order." :
    !hasAccess ? "Permission Required" : ""} arrow>
    <Box>
      <Switch
        disabled={row.punchoutOrderLocked || !hasAccess || disabled}
        color="info"
        checked={row.approved}
        onChange={() => updateOrderItemRedux(row.id, 'approved', !row.approved)}
      />
    </Box>
  </Tooltip>;
}

function BudgetCell({ row, disabled = false }) {
  BudgetCell.propTypes = {
    row: PropTypes.object,
    disabled: PropTypes.bool,
  };
  const hasAccess = useScopeCheck(['Edit-Order'], true);
  const {
    data: { budgets },
  } = useSelector((state) => state.orderSheet);
  const isBudget = budgets?.some((item) =>
    item.subbudgets?.some((subcategory) => subcategory.agoraSubcategoryId === row.agoraSubcategoryId
      || subcategory.agoraSubcategoryId === row.agoraSubcategory?.id));
  const updateOrderItemRedux = useUpdateOrderItemRedux();


  return hasAccess ? (
    <div>
      <Tooltip title={isBudget && !disabled ? (row.removedFromBudget ? 'Add to Budget' : 'Remove from Budget') : !disabled ? 'No Budget Set' : ''}>
        <span>
          <IconButton
            sx={{
              opacity: 0.8,
            }}
            disabled={!isBudget || disabled}
            onClick={() => updateOrderItemRedux(row.id, 'removedFromBudget', !row.removedFromBudget)}>
            {row.removedFromBudget || !isBudget ? (
              <MoneyOffIcon width={28} height={28} />
            ) : (
              <AttachMoneyIcon color="primary" width={28} height={28} />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </div>
  ) : (
    <></>
  )
}
const categoryDropdown = {
  field: 'categoryDropdown',
  headerName: 'Category',
  type: 'customSelect',
  onCellClick: (params) => {
    params.event.stopPropagation();
  },
  width: 120,
  renderCell: (params) => <DeletedItemTooltip deleted={params.row.deleted} children={<Category disabled={params.row.deleted} row={params.row} />} />,
  valueGetter: (params) => params.row.agoraCategory?.name || '',
  path: 'agoraCategory.name',
};
const budget = {
  field: 'budget',
  width: 120,
  headerName: 'Budget',
  renderCell: (params) => <DeletedItemTooltip
    deleted={params.row.deleted}
    children={<BudgetCell row={params.row} disabled={params.row.deleted}/>}
  />
};
const subCategoryDropdown = {
  field: 'subCategoryDropdown',
  headerName: 'Subcategory',
  type: 'customSelect',
  onCellClick: (params) => {
    params.event.stopPropagation();
  },
  width: 140,
  renderCell: (params) => <DeletedItemTooltip deleted={params.row.deleted} children={<SubCategory row={params.row} disabled={params.row.deleted} />} />,
  valueGetter: (params) => params.row.agoraSubcategory?.name || '',
  path: 'agoraSubcategory.name',
};
const category = {
  field: 'category',
  headerName: 'Category',
  type: 'customSelect',
  path: 'agoraCategory.name',
  valueGetter: (params) => params.row.agoraCategory?.name || '',
  onCellClick: (params) => {
    params.event.stopPropagation();
  },
  width: 115,
  renderCell: (params) => (
    <>
      <Tooltip title={params.row.agoraCategory?.name || ''}>
        <Typography
          // color={stringToColor(params.row.agoraCategory?.name)}
          variant="subtitle2"
          sx={{
            border: 1,
            width: '100px',
            borderRadius: '3px',
            position: 'relative',
            display: 'flex', // Use flexbox
            alignItems: 'center',
            my: '4px',
            py: '6px',
            px: 0.5,
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'hidden', // Hide overflow
              textOverflow: 'ellipsis', // Add ellipsis for overflow
              whiteSpace: 'nowrap', // Prevent wrapping
              pr: 2,
            }}
          >
            {params.row.agoraCategory.name || ''}
          </Box>
        </Typography>
      </Tooltip>
    </>
  ),
};
const subCategory = {
  field: 'subCategory',
  headerName: 'Subcategory',
  type: 'customSelect',
  path: 'agoraSubcategory.name',
  valueGetter: (params) => params.row.agoraSubcategory?.name || '',
  onCellClick: (params) => {
    params.event.stopPropagation();
  },
  width: 140,
  renderCell: (params) => (
    <>
      <Tooltip title={params.row.agoraSubcategory?.name || ''}>
        <Typography
          // color={stringToColor(params.row.agoraCategory?.name)}
          variant="subtitle2"
          sx={{
            border: 1,
            width: '125px',
            borderRadius: '3px',
            position: 'relative',
            display: 'flex', // Use flexbox
            alignItems: 'center',
            my: '4px',
            py: '6px',
            px: 0.5,
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'hidden', // Hide overflow
              textOverflow: 'ellipsis', // Add ellipsis for overflow
              whiteSpace: 'nowrap', // Prevent wrapping
              pr: 2,
            }}
          >
            {params.row.agoraSubcategory?.name || ''}
          </Box>
        </Typography>
      </Tooltip>
    </>
  ),
};
const picture = {
  field: 'picture',
  headerName: 'Image',
  width: 80,
  renderCell: (params) => <RenderImage params={params} />,
  sortable: false,
  filterable: false,
};
const pictureUrls = {
  field: 'picture',
  headerName: 'Image',
  width: 80,
  renderCell: (params) => <RenderImageFromUrls params={params} />,
  sortable: false,
  filterable: false,
};

const wideDescription = {
  field: 'description',
  headerName: 'Description',
  width: 220,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        fontSize: '0.7rem',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params.value}
    </Box>
  ),
  valueGetter: (params) => params.row.description,
  type: 'customText',
};
const description = {
  field: 'description',
  headerName: 'Description',
  width: 210,
  renderCell: (params) => {
    const descriptionContent = (
      <Box
        component="div"
        sx={{
          fontSize: '0.7rem',
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 3,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'pre-wrap',
        }}
      >
        {params.value}
      </Box>
    )
    return <DeletedItemTooltip deleted={params.row.deleted} children={descriptionContent} />
  },
  type: 'customSelect',
  valueGetter: (params) => params.row.description,
};
const vendorMessage = {
  field: 'message',
  headerName: 'Vendor Message',
  width: 120,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
        // fontSize: '0.8rem',
      }}
    >
      {params.value}
    </Box>
  ),
  valueGetter: (params) => params.row.message,
  type: 'customText',
};
const errorWide = {
  field: 'error',
  headerName: 'Error',
  width: 400,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        fontSize: '0.75rem',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'pre-wrap',
        color: 'red',
      }}
    >
      {params.row.errors?.map((error) => (
        <div key={error}>{error}</div>
      ))}
    </Box>
  ),
  type: 'customSelect',
  customFilterOperators: ['hasAnyOf', 'containsAnySubstring'],
  getFilterValues: (row) => row.errors?.map((error) => error),
  valueGetter: (params) => `${params.row.errors?.join(', ')}`,
};

const price = {
  field: 'price',
  headerName: 'Price',
  width: 100,
  renderCell: (params) => {
    const priceContent = params.row.productDetailUOMs?.length > 0 ? (
      <Box
        component="div"
        sx={{
          wordWrap: 'break-word',
          whiteSpace: 'pre-wrap',
          fontSize: '0.8rem',
        }}
      >
        {params.row.productDetailUOMs?.map((obj, index) => (
          <div key={index}>{`${fCurrency(obj.price)} - ${obj.uomAmount}/${obj.uomType}`}</div>
        ))}
      </Box>
    ) : (
      fCurrency(params.row.price)
    );
    return <DeletedItemTooltip deleted={params.row.deleted} children={priceContent} />;
  },
  valueGetter: (params) => params.row.price,
  type: 'number',
  headerAlign: 'left',
  align: 'left',
};
const perItem = {
  field: 'perItem',
  headerName: 'Per Item',
  width: 100,
  renderCell: (params) => {
    const perItemContent = params.row.productDetailUOMs?.length > 0 ? (
      <Box
        component="div"
        sx={{
          wordWrap: 'break-word',
          whiteSpace: 'pre-wrap',
          fontSize: '0.8rem',
        }}
      >
        {params.row.productDetailUOMs?.map((obj, index) => (
          <div key={index}>{`${fifthDecimalCurrency(obj.price / obj.uomAmount)}`}</div>
        ))}
      </Box>
    ) : (
      fifthDecimalCurrency(params.row.uomAmount === 0 ? params.row.price / 1 : params.row.price / params.row.uomAmount)
    );
    return <DeletedItemTooltip deleted={params.row.deleted} children={perItemContent} />;
  },
  valueGetter: (params) =>
    params.row.productDetailUOMs?.length > 0
      ? params.row.productDetailUOMs?.map((obj) => obj.price / obj.uomAmount).join(', ')
      : params.row.uomAmount === 0
        ? params.row.price / 1
        : params.row.price / params.row.uomAmount,
  type: 'number',
  headerAlign: 'left',
  align: 'left',
};

const changeUom = {
  field: 'change_uom',
  headerName: 'UOM',
  renderCell: (params) => {
    const uomAmount = params.row.uomAmount || '';
    const uomOrType = params.row.uomType || params.row.uom || '';

    const changeUomContent = params.row.productDetailUOMs?.length > 0 ? <UOMDropdown row={params.row} /> : `${uomAmount}/${uomOrType}`;
    return <DeletedItemTooltip deleted={params.row.deleted} children={changeUomContent} />;
  },
  valueGetter: (params) => `${params.row.uomAmount}/${params.row.uomType}`,
  getFilterValues: (row) => [`${row.uomAmount}/${row.uomType || row.uom}`],
  width: 90,
  type: 'customSelect',
};
const uom = {
  field: 'uom',
  headerName: 'UOM',
  renderCell: (params) => {
    const uomAmount = params.row.uomAmount || '';
    const uomOrType = params.row.uomType || params.row.uom || '';

    return `${uomAmount}/${uomOrType}`;
  },
  getFilterValues: (row) => [`${row.uomAmount}/${row.uomType}`],
  valueGetter: (params) => `${params.row.uomAmount}/${params.row.uomType}`,
  width: 80,
  type: 'customSelect',
};

const total = {
  field: 'total',
  headerName: 'Total',
  width: 80,
  renderCell: (params) => fCurrency(params.row.quantity * params.row.price),
  valueGetter: (params) => params.row.quantity * params.row.price,
};

const nonChangeTotal = {
  field: 'total',
  headerName: 'Total',
  width: 80,
  renderCell: (params) => <DeletedItemTooltip deleted={params.row.deleted} children={fCurrency(params.row.quantity * params.row.price)} />,
  valueGetter: (params) => {
    if (params.row.origQty) {
      return params.row.origQty * params.row.price;
    }

    return params.row.quantity * params.row.price;
  },
};

const previouslyOrdered = {
  field: 'previouslyOrderedItems',
  headerName: 'Prev. Ordered',
  description: 'Shows previous orders placed under this PS # only. Orders for the same item under a different PS # aren’t included.',
  width: 80,
  renderCell: (params) => <DeletedItemTooltip
    children={<PreviouslyOrderedColumn previouslyOrderedItems={params.row.previouslyOrderedItems} />}
    deleted={params.row.deleted}
  />,
  valueGetter: (params) => params.row.previouslyOrderedItems?.map((item) => item.ordered).join(', '),
};

const previouslyOrderedItems = {
  field: 'previouslyOrderedItems',
  headerName: 'History',
  width: 100,
  renderCell: (params) => (
    <Tooltip
      title={
        params.row.previouslyOrderedItems?.length > 0
          ? params.row.previouslyOrderedItems?.map((item) => (
            <div key={item.orderNumber}>
              Order# {item.orderNumber}, Qty {item.ordered}, {fMonthDayYear(item.orderedOn)}
              <br />
            </div>
          ))
          : ''
      }
    >
      <Box
        component="div"
        sx={{
          wordWrap: 'break-word',
          whiteSpace: 'pre-wrap',
        }}
      >
        {params.row.previouslyOrderedItems?.map((item) => item.ordered).join(', ')}
      </Box>
    </Tooltip>
  ),
  valueGetter: (params) => params.row.previouslyOrderedItems?.map((item) => item.ordered).join(', '),
};
const previouslyOrderedErrorItem = {
  field: 'previouslyOrdered',
  headerName: 'History',
  width: 100,
  renderCell: (params) => (
    <Tooltip
      title={
        params.row?.previouslyOrdered?.length > 0
          ? params.row.previouslyOrdered?.map((item) => (
            <div key={item.orderNumber}>
              Order# {item.orderNumber}, Qty {item.ordered}, {fMonthDayYear(item.orderedOn)}
              <br />
            </div>
          ))
          : ''
      }
    >
      <Box
        component="div"
        sx={{
          wordWrap: 'break-word',
          whiteSpace: 'pre-wrap',
        }}
      >
        {params.row.previouslyOrdered?.map((item) => item.ordered).join(', ')}
      </Box>
    </Tooltip>
  ),
  valueGetter: (params) => params.row.previouslyOrdered,
};

const vendor = {
  field: 'vendor',
  headerName: 'Vendor',
  width: 140,
  renderCell: (params) => {
    const vendorContent = (
      <Box
        component="div"
        sx={{
          wordWrap: 'break-word',
          whiteSpace: 'pre-wrap',
          fontSize: '0.7rem',
        }}
      >
        <VendorField row={params.row} />
      </Box>
    )
    return <DeletedItemTooltip deleted={params.row.deleted} children={vendorContent} />;
  },
  valueGetter: (params) => params.row.vendor,
  type: 'customSelect',
};
const vendorOnAddItem = {
  field: 'vendorName',
  headerName: 'Vendor',
  width: 100,
  // renderCell: (params) => params.row.vendorName,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
        // fontSize: '0.8rem',
      }}
    >
      {params.row.vendorName}
    </Box>
  ),
  valueGetter: (params) => params.row.vendorName,
  type: 'customSelect',
};
const vendorName = {
  field: 'vendor',
  headerName: 'Vendor',
  width: 150,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
        // fontSize: '0.8rem',
      }}
    >
      {params.row.vendor}
    </Box>
  ),
  valueGetter: (params) => params.row.vendor,
  type: 'customSelect',
};
const sku = {
  field: 'vendorItemId',
  headerName: 'SKU',
  width: 100,
  renderCell: (params) => {
    const hasLink = params.row.vendorItemLink;
    const skuContent = hasLink ? (
      <a href={params.row.vendorItemLink} target="_blank" rel="noreferrer">
        {params.value}
      </a>
    ) : (
      <Box
        component="div"
        sx={{
          wordWrap: 'break-word',
          whiteSpace: 'pre-wrap',
          '&:hover': {
            cursor: 'copy',
          },
        }}
        onClick={() => {
          navigator.clipboard.writeText(params.value);
        }}
      >
        {params.value}
      </Box>
    );
    return <DeletedItemTooltip deleted={params.row.deleted} children={skuContent} />;
  },
  type: 'customSelect',
  valueGetter: (params) => params.row.vendorItemId,
};

const productId = {
  field: 'productId',
  headerName: 'PS#',
  width: 90,
  renderCell: (params) => <DeletedItemTooltip children={<ProductItem row={params.row} disabled={params.row.deleted}/>} deleted={params.row.deleted}/>,
  valueGetter: (params) => `PS${params.row.productId}`,
  getFilterValues: (row) => [`PS${row.productId}`],
  type: 'customSelect',
};
const psNum = {
  field: 'productId',
  headerName: 'PS#',
  width: 90,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
        '&:hover': {
          cursor: 'copy',
        },
      }}
      onClick={() => {
        navigator.clipboard.writeText(params.row.productId);
      }}
    >
      PS{params.row.productId}
    </Box>
  ),
  valueGetter: (params) => `PS${params.row.productId}`,
  getFilterValues: (row) => [`PS${row.productId}`],
  type: 'customSelect',
};

const quantity = {
  field: 'quantity',
  headerName: 'Qty',
  width: 100,
  renderCell: (params) => <DeletedItemTooltip deleted={params.row.deleted} children={<QuantityField row={params.row} disabled={params.row.deleted || !params.row.approved}/>} />,
  valueGetter: (params) => params.row.origQty || params.row.quantity,
};
const quantityPlain = {
  field: 'quantity',
  headerName: 'Qty',
  width: 90,
};
const approved = {
  field: 'approved',
  headerName: 'Pending Approval',
  width: 65,
  renderCell: (params) => <DeletedItemTooltip
    deleted={params.row.deleted}
    children={<ApprovedCell row={params.row} disabled={params.row.deleted}/>}
  />,
};
const historyWithButton = {
  field: 'history',
  headerName: 'History',
  width: 65,
  renderCell: (params) => {
    const historyWithButtonContent = (
      <span className ="allow-click">
        <OrderHistoryDialog
          productId={params.row.productId}
          preSearchVendorItemId={params.row.vendorItemId}
          vendorId={params.row.vendorId}
          disabled={params.row.deleted}
        />
      </span>
    );
    return <DeletedItemTooltip deleted={params.row.deleted} children={historyWithButtonContent} />;
  },
};
const isPPD = {
  field: 'isPPD',
  headerName: 'PPD',
  renderCell: (params) => <DeletedItemTooltip deleted={params.row.deleted} children={params.value ? 'Yes' : ''} />,
  getFilterValues: (row) => (row.isPPD ? ['Yes'] : []),
  valueGetter: (params) => (params.row.isPPD ? 'Yes' : ''),
  width: 100,
  type: 'customSelect',
};
const PENDING_ORDER = [
  picture,
  description,
  categoryDropdown,
  subCategoryDropdown,
  sku,
  productId,
  previouslyOrdered,
  historyWithButton,
  perItem,
  price,
  changeUom,
  isPPD,
  vendor,
  nonChangeTotal,
  quantity,
  budget,
  approved,
];
const ADD_TO_ORDER = [
  picture,
  description,
  category,
  subCategory,
  sku,
  psNum,
  uom,
  price,
  isPPD,
  previouslyOrderedItems,
  vendorOnAddItem,
  total,
];
const ADD_CATALOG = [
  picture,
  description,
  uom,
  perItem,
  price,
  vendorOnAddItem,
  sku,
  psNum,
  category,
  subCategory,
  isPPD,
  previouslyOrderedItems,
];
const CART_CHECK = [picture, description, quantityPlain, price, total, vendorName, uom, sku, errorWide];
const CURRENT_ERROR_ITEM = [
  picture,
  wideDescription,
  uom,
  perItem,
  price,
  sku,
  psNum,
  isPPD,
  previouslyOrderedErrorItem,
  vendorName,
  quantityPlain,
  total,
  errorWide,
];
const REPLACE_ITEM = [
  picture,
  wideDescription,
  uom,
  perItem,
  price,
  sku,
  psNum,
  isPPD,
  previouslyOrderedItems,
  vendorOnAddItem,
];
const ADD_ITEM = [pictureUrls, wideDescription, uom, perItem, price, sku, vendorMessage];
const REPLACE_VENDOR_ITEM = [
  picture,
  wideDescription,
  category,
  subCategory,
  sku,
  psNum,
  uom,
  perItem,
  price,
  isPPD,
  previouslyOrdered,
  vendorName,
  quantityPlain,
  total,
];
const VENDOR_ITEM = [
  picture,
  wideDescription,
  category,
  subCategory,
  sku,
  psNum,
  uom,
  perItem,
  price,
  isPPD,
  previouslyOrderedItems,
  vendorOnAddItem,
];

export {
  ADD_CATALOG, ADD_ITEM, ADD_TO_ORDER,
  CART_CHECK, CURRENT_ERROR_ITEM, PENDING_ORDER, REPLACE_ITEM, REPLACE_VENDOR_ITEM,
  VENDOR_ITEM
};
